import React, { useMemo, useState } from "react";

import { flatten, isEmpty, join, map, some } from "lodash/fp";

import { Collapse, Stack } from "@mui/material";

import { SearchCompatibleAnnotatedText } from "@app/analytics/components/AnnotatedText";
import ExpandableSummary, {
  BUTTON_PROPS as EXPANDABLE_SUMMARY_DEFAULT_BUTTON_PROPS,
} from "@app/analytics/components/reports/ExpandableSummary";
import {
  EmptyNote,
  Note,
  NoteSubSection,
} from "@app/analytics/components/reports/styles";
import EditableContent from "@app/analytics/MeetingReport/components/EditableContent";
import UpsellOverlay from "@app/analytics/MeetingReport/components/UpsellOverlay";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import type {
  NoteSearchResult,
  SummaryItemEdit,
} from "@app/analytics/MeetingReport/types";
import {
  chunkNoteBySearchResults,
  chunkNoteBySearchResultsAndParagraphs,
} from "@app/analytics/MeetingReport/utils";
import type { TextChunk, TranscriptSummaryItem } from "@app/common/types";

import SeeMoreButton from "@rui/SeeMoreButton";

const EMPTY_SEARCH_RESULTS: NoteSearchResult[] = [];

const AnnotatedParagraph = ({
  children,
  chunks,
}: {
  children?: React.ReactNode;
  chunks: TextChunk[];
}) => (
  <Note>
    {chunks.map((chunk) => (
      <SearchCompatibleAnnotatedText
        key={chunk.uniqueKey}
        highlighted={chunk.highlighted}
        searchIndex={chunk.highlightIndex}
      >
        {chunk.content}
      </SearchCompatibleAnnotatedText>
    ))}
    {children}
  </Note>
);

const ReadonlyMeetingSummary = ({
  searchResults,
  summary,
}: {
  searchResults: NoteSearchResult[];
  summary: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  if (isEmpty(searchResults)) {
    return <ExpandableSummary value={summary} />;
  }

  const paragraphChunks = chunkNoteBySearchResultsAndParagraphs(
    summary,
    searchResults
  );
  const [firstParagraphChunks, ...remainingParagraphsChunks] = paragraphChunks;

  const forceExpand = some("highlighted", flatten(remainingParagraphsChunks));

  if (isEmpty(remainingParagraphsChunks) || forceExpand) {
    return (
      <Stack spacing={1}>
        {paragraphChunks.map((chunks) => (
          <AnnotatedParagraph
            key={join("-", map("uniqueKey", chunks))}
            chunks={chunks}
          />
        ))}
      </Stack>
    );
  }

  const toggleExpanded = () => setExpanded(!expanded);

  return (
    <>
      <AnnotatedParagraph chunks={firstParagraphChunks}>
        {/* Paragraphs already end in a "." so just add two more for a full "..." */}
        {expanded ? "" : ".. "}
        <SeeMoreButton
          {...EXPANDABLE_SUMMARY_DEFAULT_BUTTON_PROPS}
          viewingMore={expanded}
          hide={expanded}
          onClick={toggleExpanded}
        />
      </AnnotatedParagraph>
      <Collapse in={expanded}>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {remainingParagraphsChunks.map((chunks) => (
            <AnnotatedParagraph
              key={join("-", map("uniqueKey", chunks))}
              chunks={chunks}
            />
          ))}
          <SeeMoreButton
            {...EXPANDABLE_SUMMARY_DEFAULT_BUTTON_PROPS}
            viewingMore={expanded}
            onClick={toggleExpanded}
          />
        </Stack>
      </Collapse>
    </>
  );
};

const EditableSummary = ({
  summary,
  searchResults,
}: {
  summary: TranscriptSummaryItem;
  searchResults: NoteSearchResult[];
}) => {
  const { editedSummary, isEditingSummary, onCloseEdit, onFocus, saveEdit } =
    useEditStore((state) => ({
      editedSummary: state.notes.summary,
      isEditingSummary: state.notes.activeItem?.id === summary.id,
      onCloseEdit: state.exitItem,
      onFocus: state.startEditingItem,
      saveEdit: state.save,
    }));

  const handleSaveEdits = (updatedValue: string) => {
    if (summary.value === updatedValue) return;

    const edit: SummaryItemEdit = {
      type: "summary",
      before: editedSummary,
      after: { ...editedSummary, value: updatedValue },
    };
    saveEdit(edit);
  };

  const handleToggleEditing = (editing: boolean) => {
    if (editing) {
      onFocus({ type: "summary", item: summary });
    } else {
      onCloseEdit();
    }
  };

  const contentChunks = useMemo(() => {
    if (searchResults.length) {
      return chunkNoteBySearchResults(editedSummary.value, searchResults);
    }
    return editedSummary.value;
  }, [searchResults, editedSummary.value]);

  return (
    <EditableContent
      editing={isEditingSummary}
      onSave={handleSaveEdits}
      onToggleEditing={handleToggleEditing}
      value={editedSummary.value}
      type="Summary"
      contentChunks={contentChunks}
    />
  );
};

const MeetingSummary = ({
  shouldUpsell,
  summary,
}: {
  shouldUpsell: boolean;
  summary: TranscriptSummaryItem;
}) => {
  const searchResults = useSearchStore(
    (state) =>
      state.searchResultsByNoteTypeAndId.summary.get(summary.id) ??
      EMPTY_SEARCH_RESULTS
  );

  const isEditing = useEditStore(
    (state) =>
      state.editingSection === "notes" && state.editAccessLevel === "full"
  );

  if (shouldUpsell) {
    return (
      <NoteSubSection>
        <UpsellOverlay>
          <Note>{summary.value}</Note>
        </UpsellOverlay>
      </NoteSubSection>
    );
  }

  if (isEmpty(summary.value)) {
    return (
      <NoteSubSection>
        <EmptyNote>No summary generated</EmptyNote>
      </NoteSubSection>
    );
  }

  if (isEditing) {
    return (
      <NoteSubSection>
        <EditableSummary summary={summary} searchResults={searchResults} />
      </NoteSubSection>
    );
  }

  return (
    <NoteSubSection>
      <ReadonlyMeetingSummary
        searchResults={searchResults}
        summary={summary.value}
      />
    </NoteSubSection>
  );
};

export default MeetingSummary;
